import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import "../../../assets/css/gamesCss/Array/intro2D.css";
import { useDarkMode } from "../../../context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";



const Intro2D = () => {
    const [gameStarted, setGameStarted] = useState(false);
    const [lives, setLives] = useState(3);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [array2D, setArray2D] = useState([[null, null, null], [null, null, null]]);
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [timer, setTimer] = useState(20);
    const [shuffledQuestions, setShuffledQuestions] = useState([]);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [animatingCell, setAnimatingCell] = useState(null);
    const { darkMode } = useDarkMode();
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();


    const questions = [
        {
            question: selLanguage
                ? "Ποια από τις παρακάτω δομές δεδομένων ΔΕΝ μπορεί να αναπαρασταθεί αποδοτικά με 2D πίνακα;"
                : "Which of the following data structures CANNOT be efficiently represented with a 2D array?",
            options: selLanguage
                ? ["Πίνακας συσχέτισης (correlation matrix)", "Δυαδικό δέντρο αναζήτησης", "Πίνακας adjacency για γράφο", "Εικόνα RGB"]
                : ["Correlation matrix", "Binary search tree", "Adjacency matrix for graph", "RGB image"],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Σε έναν 2D πίνακα 5×7, ποια είναι η πολυπλοκότητα χρόνου για την εύρεση του μέγιστου στοιχείου;"
                : "In a 5×7 2D array, what is the time complexity for finding the maximum element?",
            options: selLanguage
                ? ["O(n)", "O(n²)", "O(n log n)", "O(n·m) όπου n=5, m=7"]
                : ["O(n)", "O(n²)", "O(n log n)", "O(n·m) where n=5, m=7"],
            impostor: 2
        },
        {
            question: selLanguage
                ? "Ποια από τις παρακάτω προτάσεις είναι ΛΑΘΟΣ για τους 2D πίνακες στη C;"
                : "Which of the following statements about 2D arrays in C is FALSE?",
            options: selLanguage
                ? [
                    "Αποθηκεύονται σε συνεχόμενη μνήμη (row-major order)",
                    "Μπορούν να έχουν διαφορετικό μήκος γραμμών (jagged arrays)",
                    "Ο τύπος int arr[3][4] δηλώνει 3 γραμμές και 4 στήλες",
                    "Η πρόσβαση arr[i][j] μεταφράζεται σε *(*(arr+i)+j)"
                ]
                : [
                    "Stored in contiguous memory (row-major order)",
                    "They can have variable-length rows (jagged arrays)",
                    "int arr[3][4] means 3 rows and 4 columns",
                    "arr[i][j] translates to *(*(arr+i)+j)"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Ποια τεχνική ΔΕΝ χρησιμοποιείται για βελτιστοποίηση πρόσβασης σε μεγάλους 2D πίνακες;"
                : "Which technique is NOT used to optimize access on large 2D arrays?",
            options: selLanguage
                ? [
                    "Cache-friendly traversal (row-major order)",
                    "Loop unrolling",
                    "Ταξινόμηση των στοιχείων πριν την πρόσβαση",
                    "Blocking/Tiling για cache optimization"
                ]
                : [
                    "Cache-friendly traversal (row-major order)",
                    "Loop unrolling",
                    "Sorting elements before access",
                    "Blocking/Tiling for cache optimization"
                ],
            impostor: 2
        },
        {
            question: selLanguage
                ? "Σε Python, ποιος τρόπος είναι ο ΛΙΓΟΤΕΡΟ αποδοτικός για δημιουργία 2D array 1000×1000;"
                : "In Python, which method is the LEAST efficient for creating a 1000×1000 2D array?",
            options: selLanguage
                ? [
                    "numpy.zeros((1000, 1000))",
                    "[[0]*1000 for _ in range(1000)]",
                    "[[0 for _ in range(1000)] for _ in range(1000)]",
                    "[[0]*1000]*1000"
                ]
                : [
                    "numpy.zeros((1000, 1000))",
                    "[[0]*1000 for _ in range(1000)]",
                    "[[0 for _ in range(1000)] for _ in range(1000)]",
                    "[[0]*1000]*1000"
                ],
            impostor: 3
        },
        {
            question: selLanguage
                ? "Ποια από τις παρακάτω εφαρμογές ΔΕΝ ενδείκνυται για 2D πίνακες;"
                : "Which of the following applications is NOT suitable for 2D arrays?",
            options: selLanguage
                ? [
                    "Αλγόριθμος Floyd-Warshall για shortest paths",
                    "Αποθήκευση sparse matrix με πολλά μηδενικά",
                    "Convolution σε image processing",
                    "Dynamic programming για Longest Common Subsequence"
                ]
                : [
                    "Floyd–Warshall algorithm for shortest paths",
                    "Storing sparse matrices with many zeros",
                    "Convolution in image processing",
                    "Dynamic programming for Longest Common Subsequence"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Για πολλαπλασιασμό δύο πινάκων n×n, ποια είναι η βέλτιστη γνωστή πολυπλοκότητα;"
                : "For multiplying two n×n matrices, what is the best-known complexity?",
            options: selLanguage
                ? ["O(n³)", "O(n²·⁸¹)", "O(n² log n)", "O(n²)"]
                : ["O(n³)", "O(n².81)", "O(n² log n)", "O(n²)"],
            impostor: 0
        },
        {
            question: selLanguage
                ? "Ποιο από τα παρακάτω ΔΕΝ είναι πλεονέκτημα των 2D πινάκων;"
                : "Which of the following is NOT an advantage of 2D arrays?",
            options: selLanguage
                ? [
                    "Γρήγορη πρόσβαση σε τυχαίο στοιχείο O(1)",
                    "Καλή χρήση cache locality",
                    "Εύκολη προσθήκη/αφαίρεση γραμμών",
                    "Απλή αναπαράσταση grid-based δεδομένων"
                ]
                : [
                    "Fast random access O(1)",
                    "Good cache locality",
                    "Easy row insertion/deletion",
                    "Simple representation of grid-based data"
                ],
            impostor: 2
        },
        {
            question: selLanguage
                ? "Σε έναν 2D πίνακα που αναπαριστά γράφο adjacency matrix, τι δείχνει το arr[i][j] = 1;"
                : "In a 2D adjacency matrix for a graph, what does arr[i][j] = 1 indicate?",
            options: selLanguage
                ? [
                    "Υπάρχει ακμή από κόμβο i σε κόμβο j",
                    "Ο κόμβος i έχει βάρος 1",
                    "Υπάρχει μονοπάτι μεταξύ i και j",
                    "Ο κόμβος j είναι γονέας του i"
                ]
                : [
                    "There is an edge from node i to node j",
                    "Node i has weight 1",
                    "There is a path between i and j",
                    "Node j is the parent of node i"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Ποια από τις παρακάτω πράξεις έχει τη ΧΕΙΡΟΤΕΡΗ πολυπλοκότητα σε 2D πίνακα n×m;"
                : "Which of the following operations has the WORST complexity on an n×m 2D array?",
            options: selLanguage
                ? [
                    "Εύρεση ελάχιστου στοιχείου",
                    "Αναζήτηση συγκεκριμένης τιμής (unsorted)",
                    "Αναζήτηση σε sorted row και column matrix",
                    "Ανάγνωση όλων των στοιχείων"
                ]
                : [
                    "Find minimum element",
                    "Searching for a value (unsorted)",
                    "Searching in row+column sorted matrix",
                    "Reading all elements"
                ],
            impostor: 2
        },
        {
            question: selLanguage
                ? "Ποιο pattern traversal ΔΕΝ είναι κοινό στους 2D πίνακες;"
                : "Which traversal pattern is NOT common in 2D arrays?",
            options: selLanguage
                ? [
                    "Row-major (γραμμή προς γραμμή)",
                    "Column-major (στήλη προς στήλη)",
                    "Diagonal traversal",
                    "Random access traversal"
                ]
                : [
                    "Row-major",
                    "Column-major",
                    "Diagonal traversal",
                    "Random access traversal"
                ],
            impostor: 3
        },
        {
            question: selLanguage
                ? "Για αποθήκευση εικόνας 1920×1080 RGB, πόσα bytes χρειάζονται (χωρίς compression);"
                : "How many bytes are needed to store a 1920×1080 RGB image (uncompressed)?",
            options: selLanguage
                ? ["2,073,600 bytes", "6,220,800 bytes", "8,294,400 bytes", "2,764,800 bytes"]
                : ["2,073,600 bytes", "6,220,800 bytes", "8,294,400 bytes", "2,764,800 bytes"],
            impostor: 0
        },
        {
            question: selLanguage
                ? "Ποια δήλωση ΔΕΝ είναι έγκυρη σε C για 2D πίνακα;"
                : "Which declaration is NOT valid in C for a 2D array?",
            options: selLanguage
                ? [
                    "int arr[3][4] = {{1,2},{3,4}};",
                    "int arr[][4] = {{1,2,3,4},{5,6,7,8}};",
                    "int arr[3][] = {{1,2},{3,4},{5,6}};",
                    "int arr[2][3] = {1,2,3,4,5,6};"
                ]
                : [
                    "int arr[3][4] = {{1,2},{3,4}};",
                    "int arr[][4] = {{1,2,3,4},{5,6,7,8}};",
                    "int arr[3][] = {{1,2},{3,4},{5,6}};",
                    "int arr[2][3] = {1,2,3,4,5,6};"
                ],
            impostor: 2
        },
        {
            question: selLanguage
                ? "Ποια τεχνική χρησιμοποιείται για αποδοτική αποθήκευση sparse 2D matrices;"
                : "Which technique is used for efficient storage of sparse 2D matrices?",
            options: selLanguage
                ? [
                    "Αποθήκευση μόνο μη-μηδενικών στοιχείων με (row, col, value)",
                    "Χρήση πλήρους 2D array",
                    "Χρήση linked list",
                    "Ταξινόμηση των στοιχείων"
                ]
                : [
                    "Store only non-zero elements as (row, col, value)",
                    "Use a full 2D array",
                    "Use linked lists",
                    "Sort the elements"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Σε έναν αλγόριθμο dynamic programming με 2D πίνακα, τι συμβαίνει συνήθως;"
                : "In a dynamic programming algorithm using a 2D matrix, what typically happens?",
            options: selLanguage
                ? [
                    "Κάθε κελί υπολογίζεται από προηγούμενα κελιά",
                    "Τα κελιά γεμίζουν τυχαία",
                    "Χρησιμοποιείται μόνο η διαγώνιος",
                    "Υπολογίζεται μόνο το τελευταίο κελί"
                ]
                : [
                    "Each cell is computed from previous cells",
                    "Cells are filled randomly",
                    "Only the diagonal is used",
                    "Only the final cell is computed"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Ποια είναι η διαφορά μεταξύ int arr[3][4] και int* arr[3] στη C;"
                : "What is the difference between int arr[3][4] and int* arr[3] in C?",
            options: selLanguage
                ? [
                    "Το πρώτο είναι συνεχής μνήμη, το δεύτερο array από pointers",
                    "Είναι το ίδιο",
                    "Το δεύτερο είναι ταχύτερο",
                    "Το πρώτο δεν μπορεί να τροποποιηθεί"
                ]
                : [
                    "The first is contiguous memory, the second is an array of pointers",
                    "They are the same",
                    "The second is faster",
                    "The first cannot be modified"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Για rotation εικόνας 90° χρησιμοποιώντας 2D array, ποιος μετασχηματισμός χρειάζεται;"
                : "For rotating an image 90° using a 2D array, which transformation is used?",
            options: selLanguage
                ? [
                    "arr[i][j] → arr[j][n-1-i]",
                    "arr[i][j] → arr[j][i]",
                    "arr[i][j] → arr[n-1-i][j]",
                    "arr[i][j] → arr[i][n-1-j]"
                ]
                : [
                    "arr[i][j] → arr[j][n-1-i]",
                    "arr[i][j] → arr[j][i]",
                    "arr[i][j] → arr[n-1-i][j]",
                    "arr[i][j] → arr[i][n-1-j]"
                ],
            impostor: 1
        },
        {
            question: selLanguage
                ? "Ποιο είναι το ΜΕΙΟΝΕΚΤΗΜΑ των 2D πινάκων για gaming boards (π.χ. σκάκι);"
                : "What is a DISADVANTAGE of using 2D arrays for gaming boards (e.g., chess)?",
            options: selLanguage
                ? [
                    "Δύσκολη πρόσβαση στα στοιχεία",
                    "Υψηλή πολυπλοκότητα αναζήτησης",
                    "Σταθερό μέγεθος board",
                    "Δεν υποστηρίζουν διαγώνιες κινήσεις"
                ]
                : [
                    "Hard access to elements",
                    "High search complexity",
                    "Fixed board size",
                    "No diagonal moves supported"
                ],
            impostor: 0
        },
        {
            question: selLanguage
                ? "Σε matrix transposition, ποια είναι η in-place πολυπλοκότητα χώρου;"
                : "In matrix transposition, what is the in-place space complexity?",
            options: selLanguage
                ? ["O(n²)", "O(1)", "O(n)", "O(log n)"]
                : ["O(n²)", "O(1)", "O(n)", "O(log n)"],
            impostor: 0
        },
        {
            question: selLanguage
                ? "Ποια πρόταση για memory layout είναι ΣΩΣΤΗ;"
                : "Which statement about memory layout is TRUE?",
            options: selLanguage
                ? [
                    "Η Python χρησιμοποιεί row-major order",
                    "Η C χρησιμοποιεί row-major, η Fortran column-major",
                    "Όλες οι γλώσσες χρησιμοποιούν το ίδιο layout",
                    "Το layout δεν επηρεάζει την απόδοση"
                ]
                : [
                    "Python always uses row-major order",
                    "C uses row-major, Fortran uses column-major",
                    "All languages use the same layout",
                    "Layout does not affect performance"
                ],
            impostor: 0
        }
    ];

    // Shuffle questions on mount
    useEffect(() => {
        const shuffled = [...questions].sort(() => Math.random() - 0.5);
        setShuffledQuestions(shuffled);
    }, []);

    useEffect(() => {
        if (!gameStarted || showResult || timer === 0) return;

        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev <= 1) {
                    handleTimeout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [gameStarted, showResult, timer]);

    // Check game over
    useEffect(() => {
        if (lives <= 0) {
            setShowGameOver(true);
        }
    }, [lives]);

    const handleTimeout = () => {
        setIsCorrect(false);
        setShowResult(true);
        setLives(prev => prev - 1);
    };

    const handleAnswerClick = (index) => {
        if (showResult) return;

        setSelectedAnswer(index);
        const currentQuestion = shuffledQuestions[currentQuestionIndex];
        const correct = index === currentQuestion.impostor;

        setIsCorrect(correct);
        setShowResult(true);

        if (correct) {
            // Find next empty cell and animate
            let cellFound = false;
            for (let i = 0; i < 2 && !cellFound; i++) {
                for (let j = 0; j < 3 && !cellFound; j++) {
                    if (array2D[i][j] === null) {
                        setAnimatingCell({ row: i, col: j });
                        setTimeout(() => {
                            const newArray = array2D.map(row => [...row]);
                            newArray[i][j] = currentQuestionIndex + 1;
                            setArray2D(newArray);
                            setAnimatingCell(null);

                            // Check if array is full
                            if (newArray.every(row => row.every(cell => cell !== null))) {
                                setTimeout(() => setShowGameWon(true), 500);
                            }
                        }, 1000);
                        cellFound = true;
                    }
                }
            }
        } else {
            setLives(prev => prev - 1);
        }
    };

    const handleNextQuestion = () => {
        if (currentQuestionIndex < shuffledQuestions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
            setSelectedAnswer(null);
            setShowResult(false);
            setTimer(20);
        }
    };

    const startGame = () => {
        setGameStarted(true);
        setTimer(20);
    };

    const resetGame = () => {
        setGameStarted(false);
        setLives(3);
        setCurrentQuestionIndex(0);
        setArray2D([[null, null, null], [null, null, null]]);
        setSelectedAnswer(null);
        setShowResult(false);
        setTimer(20);
        setShowGameOver(false);
        setShowGameWon(false);
        setAnimatingCell(null);
        const shuffled = [...questions].sort(() => Math.random() - 0.5);
        setShuffledQuestions(shuffled);
    };

    const currentQuestion = shuffledQuestions[currentQuestionIndex];

    return (
        <div
            className="main-content intro2DContainer"
            style={{
                background: darkMode ? "#1E293B" : "var(--customBuildButton)",
            }}
        >
            <GameOverModal isVisible={showGameOver} onPlayAgain={resetGame} />
            <GameWonModal isVisible={showGameWon} onPlayAgain={resetGame} />

            <div className="introContainerGame">
                {/* Lives Display */}
                <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginBottom: "20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>


                        <div style={{ display: "flex", gap: "6px" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "24px",
                                        filter: lives >= heartNum ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))" : "grayscale(100%) opacity(0.3)",
                                        transform: lives >= heartNum ? "scale(1)" : "scale(0.8)",
                                        transition: "all 0.3s ease"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {!gameStarted ? (
                    <div className="introScreen2D">
                        <div className="intro2Dprom">
                            <div>
                                <p><strong>{selLanguage?"Στόχος:":"Goal:"}</strong> 
                                {selLanguage?"Γέμισε τον 2D πίνακα 2×3 βρίσκοντας τις λάθος απαντήσεις!":
                                "Fill the 2D array 2×3 by finding the impostor answers!"}
                                </p>
                                <p><strong>{selLanguage?"Κανόνες:":"Rules:"}
                                </strong></p>
                                <ul>
                                    <li>{selLanguage?"Κάθε ερώτηση έχει 4 επιλογές και 1 είναι λάθος (impostor)":
                                        "Each question has 4 options and 1 is the impostor answer"}</li>
                                    <li>{selLanguage?"Έχεις 20 δευτερόλεπτα για κάθε ερώτηση"
                                        :"You have 20 seconds for each question"}</li>
                                    <li>{selLanguage?"Κάθε σωστή απάντηση προσθέτει στοιχείο στον πίνακα"
                                        :"Each correct answer fills a cell in the array"}</li>
                                    <li>{selLanguage?"Έχεις 3 ζωές !":
                                        "You have 3 lives "}</li>
                                    <li>{selLanguage?"Νίκη: Γέμισε όλο τον πίνακα (6 σωστές απαντήσεις)"
                                        :"Win: Fill the entire array (6 correct answers)"}</li>
                                </ul>
                            </div>
                        </div>
                        <button onClick={startGame} className="intro2DStartButton">
                            {selLanguage?"Ξεκίνα το Παιχνίδι":"Start Game"}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* 2D Array Display */}
                        <div className="intro2DArrayContainer">
                            <h3 className="intro2Dtitle">2D Array [2][3]</h3>
                            <div className="intro2Dgrid">
                                {array2D.map((row, i) => (
                                    <div key={i} className="intro2DRow">
                                        {row.map((cell, j) => (
                                            <motion.div
                                                key={`${i}-${j}`}
                                                className="intro2Dcell"
                                                initial={{ scale: 1 }}
                                                animate={
                                                    animatingCell?.row === i && animatingCell?.col === j
                                                        ? { scale: [1, 1.5, 1], backgroundColor: ["#3b82f6", "#10b981", "#3b82f6"] }
                                                        : { scale: 1 }
                                                }
                                                transition={{ duration: 1 }}
                                            >
                                                {cell !== null ? (
                                                    <motion.span
                                                        initial={{ scale: 0, rotate: -180 }}
                                                        animate={{ scale: 1, rotate: 0 }}
                                                        transition={{ type: "spring", duration: 0.5 }}
                                                    >
                                                        ✓
                                                    </motion.span>
                                                ) : (
                                                    <span className="intro2DemptyCell">_</span>
                                                )}
                                            </motion.div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Question Section */}
                        {currentQuestion && (
                            <div className="intro2DquestionSection">
                                <div className="intro2DTimerBar" style={{
                                    width: `${(timer / 20) * 100}%`,
                                    background: timer > 8
                                        ? "linear-gradient(90deg, #3b82f6 0%, #3b82f6 100%)" // μπλε για 20->12
                                        : "linear-gradient(90deg, #ef4444 0%, #ef4444 100%)", // κόκκινη για 12->0

                                    transition: "width 1s linear, background 0.5s ease"
                                }}>
                                    <div className="intro2DTimerFill"
                                        style={{ width: `${(timer / 20) * 100}%` }}></div>
                                    <span className="intro2DTimerNext"> {timer}s</span>
                                </div>

                                <h2 className="intro2DQuestionText" style={{ color: darkMode ? "white" : "#1E293B" }}>{currentQuestion.question}</h2>

                                <div className="intro2DOptionsGrid">
                                    {currentQuestion.options.map((option, index) => (
                                        <motion.button
                                            key={index}
                                            className={`intro2DOptionsButton ${showResult
                                                ? index === currentQuestion.impostor
                                                    ? "impostor-revealed"
                                                    : selectedAnswer === index
                                                        ? "wrong-answer"
                                                        : ""
                                                : selectedAnswer === index
                                                    ? "selected"
                                                    : ""
                                                }`}
                                            onClick={() => handleAnswerClick(index)}
                                            disabled={showResult}
                                            whileHover={{ scale: showResult ? 1 : 1.02 }}
                                            whileTap={{ scale: showResult ? 1 : 0.98 }}
                                        >
                                            {option}
                                            {showResult && index === currentQuestion.impostor && " 🎯"}
                                        </motion.button>
                                    ))}
                                </div>

                                {showResult && (
                                    <motion.div style={{ display: "flex", justifyContent: "center", width: "100%" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                        <button onClick={handleNextQuestion} className="intro2DNextQuestionbtn">
                                            {selLanguage?"Επόμενη Ερώτηση":"Next Question"}
                                        </button>
                                    </motion.div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default Intro2D;